/**
 * Page-editor adapter for the headless Review moment.
 *
 * Chat owns the last-message chrome (`ReviewMomentFooter`). This bridge only
 * forwards enabled state and undo / redo / diff callbacks into the shared
 * slot. Staging is a same-origin iframe, so the payload must stay serializable
 * flags + `onAction` (no editor-created React tree).
 *
 * Renders nothing.
 */
import { useCallback, useEffect, useRef } from 'react';

import {
	insertReviewMomentFooter,
	removeReviewMomentFooter,
} from '@atlassian/conversation-assistant/controllers/message-footer-slot/insertReviewMomentFooter';
import { getFooterSlotGeneration } from '@atlassian/conversation-assistant/controllers/message-footer-slot/messageFooterSlotStore';

export const ReviewChatBridge = ({
	available,
	canRedo,
	canUndo,
	isDisplayingDiff,
	onRedo,
	onToggleDiff,
	onUndo,
}: {
	available: boolean;
	canRedo: boolean;
	canUndo: boolean;
	isDisplayingDiff: boolean;
	onRedo: () => void;
	onToggleDiff: () => void;
	onUndo: () => void;
}): null => {
	// Capture at mount so a later create / conversation change (which bumps
	// the window.top store generation) cannot re-insert this editor's Undo
	// into chat, and so unmount cannot remove a newer document's footer.
	const generationRef = useRef(getFooterSlotGeneration());

	const onUndoRef = useRef(onUndo);
	onUndoRef.current = onUndo;
	const onRedoRef = useRef(onRedo);
	onRedoRef.current = onRedo;
	const onToggleDiffRef = useRef(onToggleDiff);
	onToggleDiffRef.current = onToggleDiff;

	const onRemoteAction = useCallback((actionId: string) => {
		if (actionId === 'undo') {
			onUndoRef.current();
			return;
		}
		if (actionId === 'redo') {
			onRedoRef.current();
			return;
		}
		if (actionId === 'toggle-diff') {
			onToggleDiffRef.current();
		}
	}, []);

	useEffect(() => {
		if (!available && !isDisplayingDiff) {
			removeReviewMomentFooter(generationRef.current);
			return;
		}

		insertReviewMomentFooter(
			{
				canRedo,
				canUndo,
				isDisplayingDiff,
			},
			onRemoteAction,
			generationRef.current,
		);
	}, [available, canRedo, canUndo, isDisplayingDiff, onRemoteAction]);

	useEffect(() => {
		const generation = generationRef.current;
		return () => {
			removeReviewMomentFooter(generation);
		};
	}, []);

	return null;
};
